<template>
  <v-menu v-model="menu" :close-on-content-click="false" location="bottom">
    <template v-slot:activator="{ props }">
      <v-chip v-bind="props" class="date-chip" variant="outlined">
        {{ label }}: {{ dateDisplay || '--' }}
      </v-chip>
    </template>

    <v-card class="date-picker-card">
      <v-date-picker
        width="360"
        v-model="date"
        v-model:view-mode="view"
        :month="month"
        :year="year"
        @update:month="val => month = val"
        @update:year="val => year = val"
        :min="min"
        :max="max"
        show-adjacent-months
      >
        <template v-slot:header>
          <div class="date-picker__header d-flex pa-2 w-100">
            <div class="date-picker__header_selector d-flex align-center flex-1">
              <v-btn
                icon="mdi-chevron-left"
                variant="text"
                size="small"
                density="compact"
                @click="prevMonth"
              />
              <v-btn
                variant="text"
                class="text-capitalize d-flex align-center"
                @click="showMonths"
              >
                {{ monthName }}
                <v-icon
                  :icon="view === 'months' ? 'mdi-menu-up' : 'mdi-menu-down'"
                  size="default"
                  class="ml-1"
                />
              </v-btn>
              <v-btn
                icon="mdi-chevron-right"
                variant="text"
                size="small"
                density="compact"
                @click="nextMonth"
              />
            </div>

            <div class="date-picker__header_selector d-flex align-center flex-1">
              <v-btn
                icon="mdi-chevron-left"
                variant="text"
                size="small"
                density="compact"
                @click="year--"
              />
              <v-btn
                variant="text"
                class="d-flex align-center"
                @click="showYears"
              >
                {{ year }}
                <v-icon
                  :icon="view === 'year' ? 'mdi-menu-up' : 'mdi-menu-down'"
                  size="default"
                  class="ml-1"
                />
              </v-btn>
              <v-btn
                icon="mdi-chevron-right"
                variant="text"
                size="small"
                density="compact"
                @click="year++"
              />
            </div>
          </div>
        </template>
      </v-date-picker>

      <v-divider />
      <v-card-actions class="justify-space-between py-2 px-3">
        <v-btn
          class="px-6 py-5 d-flex"
          variant="text"
          color="primary-35"
          @click="clear"
        >
          Limpar
        </v-btn>
        <v-spacer />
        <v-btn
          class="px-6 py-5 d-flex"
          rounded="xl"
          variant="outlined"
          color="neutral-35"
          @click="menu = false"
        >
          Cancelar
        </v-btn>
        <v-btn
          variant="elevated"
          class="px-6 py-5 d-flex"
          rounded="xl"
          color="primary-60"
          @click="confirm"
        >
          Confirmar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits, watch } from 'vue';

const props = defineProps({
    modelValue: {
        type: [Date, String, null],
        default: null,
    },
    label: {
        type: String,
        default: 'Selecionar Data',
    },
    min: {
        type: [Date, String, null],
        default: null,
    },
    max: {
        type: [Date, String, null],
        default: null,
    },
});

const emit = defineEmits(['update:modelValue']);

const menu = ref(false);
const date = ref(props.modelValue ? new Date(props.modelValue) : null);
const month = ref(new Date().getMonth());
const year = ref(new Date().getFullYear());
const view = ref('month');

watch(
    () => props.modelValue,
    (val) => {
        date.value = val ? new Date(val) : null;
    }
);

watch(menu, (open) => {
    if (!open) {
        clear();
        return
    }

    const dateToShow = date.value ? new Date(date.value) : new Date();
    month.value = dateToShow.getMonth();
    year.value = dateToShow.getFullYear();
    view.value = 'month';
});

const prevMonth = () => {
    if (month.value === 0) {
        month.value = 11;
        year.value--;
        return;
    }
    month.value--;
};

const nextMonth = () => {
    if (month.value === 11) {
        month.value = 0;
        year.value++;
        return;
    }
    month.value++;
};

const showMonths = () => {
    view.value = view.value === 'months' ? 'month' : 'months';
};

const showYears = () => {
    view.value = view.value === 'year' ? 'month' : 'year';
};

const dateDisplay = computed(() => {
    if (!props.modelValue) return null;
    return new Date(props.modelValue).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
});

const monthName = computed(() => {
    const name = new Date(year.value, month.value).toLocaleString('pt-BR', { month: 'long' });
    return name.charAt(0).toUpperCase() + name.slice(1);
});

const clear = () => {
    date.value = null;
};

const confirm = () => {
    emit('update:modelValue', date.value);
    menu.value = false;
};


</script>

<style scoped lang="scss">
.date-chip {
    height: 32px;
    border-color: #c4c7c6;
    background-color: #f8faf9;
    border-radius: 8px;
    font-weight: 500;
    font-size: 14px;
    color: #505353;
    cursor: pointer;
    transition: background-color 0.2s ease-in-out;
}

.date-picker-card {
    min-width: 360px;
    border-radius: 16px !important;
}

.date-picker__header{
  margin-left: 6.5px;
  color: #49454F;
}

.date-picker__header_selector{
  width: 100%;
  flex: 1;
  display: flex;
  justify-content: center;
}

.v-date-picker {
    :deep(.text-capitalize) {
        text-transform: capitalize;
    }

    :deep(.v-date-picker-controls) {
        display: none;
    }

    :deep(.v-picker-title) {
        display: none;
    }

    :deep(.v-date-picker-month__day--selected > .v-date-picker-month__day-btn) {
        background-color: #7f92ae;
    }

    :deep(.v-btn--variant-outlined){
        border-color: #7f92ae;
    }

    :deep(.v-date-picker-month){
      padding: 0px;
    }
}
</style>
